import { useState } from 'react';
import { useMutation } from './query.js';
import { api, ApiError, type GeneratedQuestion, type NewQuestionResponse } from './api.js';

/**
 * M10g — "Suggest new question" dialog for a concept (1/2/3-mark, intra-cluster).
 *
 * Flow: pick mark_value (1/2/3) + command_word → Generate (POST /api/ai/new-question,
 * NO DB write) → preview the returned draft read-only → Save (POST /api/template,
 * inserts a DRAFT/pending SYNTHETIC row tagged to the concept) / Regenerate / Discard.
 *
 * Generation never writes the DB; only Save does. On Save success the parent
 * invalidates the concept caches so the new DRAFT appears in the questions card.
 */

// Command words sensible for short-answer 1/2/3-mark concept questions.
const COMMAND_WORD_OPTIONS = ['Define', 'State', 'Calculate', 'Explain', 'MCQ'] as const;

function errDetail(e: unknown): string {
  const err = e as ApiError;
  if (err.body && typeof err.body === 'object' && 'detail' in err.body) {
    return String((err.body as Record<string, unknown>).detail);
  }
  return err.message;
}

export function NewQuestionModal({
  clusterId,
  conceptCode,
  onClose,
  onSaved,
}: {
  clusterId: string;
  conceptCode: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [marks, setMarks] = useState<number>(3);
  const [commandWord, setCommandWord] = useState<string>('Explain');
  const [draft, setDraft] = useState<GeneratedQuestion | null>(null);

  const generate = useMutation({
    mutationFn: () =>
      api.post<NewQuestionResponse>('/api/ai/new-question', {
        cluster_id: clusterId,
        concept_code: conceptCode,
        mark_value: marks,
        command_word: commandWord,
      }),
    onSuccess: (res) => setDraft(res.question),
  });

  const save = useMutation({
    mutationFn: () =>
      api.post('/api/template', {
        cluster_id: clusterId,
        concept_code: conceptCode,
        mark_value: marks,
        command_word: commandWord,
        question: draft,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const isMcq = commandWord === 'MCQ';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="modal-new-question">
        <h2 style={{ marginTop: 0 }}>
          Suggest new question — <span style={{ fontFamily: 'var(--mono)' }}>{conceptCode}</span>
        </h2>

        <div className="field-grid">
          <div>
            <label>Marks</label>
            <select
              value={marks}
              onChange={(e) => setMarks(Number(e.target.value))}
              disabled={generate.isPending || save.isPending}
              data-testid="select-new-question-marks"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>
          <div>
            <label>Command word</label>
            <select
              value={commandWord}
              onChange={(e) => setCommandWord(e.target.value)}
              disabled={generate.isPending || save.isPending}
              data-testid="select-new-question-command-word"
            >
              {COMMAND_WORD_OPTIONS.map((cw) => (
                <option key={cw} value={cw}>
                  {cw}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row-actions" style={{ marginTop: 10 }}>
          <button
            className="primary"
            type="button"
            onClick={() => generate.mutate()}
            disabled={generate.isPending || save.isPending}
            data-testid="button-new-question-generate"
          >
            {generate.isPending ? 'Generating…' : draft ? 'Regenerate' : 'Generate'}
          </button>
          <button type="button" onClick={onClose} disabled={save.isPending} data-testid="button-new-question-discard">
            Discard
          </button>
          {generate.isError && (
            <span className="err" data-testid="new-question-generate-error">
              Generation failed: {errDetail(generate.error)}
            </span>
          )}
        </div>

        {draft && (
          <div className="read-view" data-testid="new-question-preview" style={{ marginTop: 14 }}>
            <h3 style={{ fontSize: 13, margin: '4px 0' }}>Preview ({draft.marks}m · {draft.command_word})</h3>
            <p className="question-stem" style={{ whiteSpace: 'pre-wrap' }}>{draft.stem}</p>

            {isMcq && draft.distractors && draft.distractors.length > 0 ? (
              <ol className="mcq-options" type="A">
                {draft.distractors.map((d) => (
                  <li key={d.option} className={d.is_correct ? 'correct' : ''}>
                    {d.text}
                    {d.is_correct && <span className="chip genuine" style={{ marginLeft: 6 }}>correct</span>}
                  </li>
                ))}
              </ol>
            ) : (
              <>
                <table className="mark-scheme-table">
                  <thead>
                    <tr>
                      <th>Marks</th>
                      <th>Criterion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.mark_scheme.points.map((p, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: 'var(--mono)' }}>{p.marks}</td>
                        <td>{p.criterion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(draft.mark_scheme.indicative_content?.length ?? 0) > 0 && (
                  <>
                    <h3 style={{ fontSize: 13, margin: '10px 0 4px' }}>Indicative content</h3>
                    <ul>
                      {draft.mark_scheme.indicative_content!.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </>
                )}
                {draft.model_answer && (
                  <details className="model-answer" open>
                    <summary>Model answer</summary>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{draft.model_answer}</p>
                  </details>
                )}
              </>
            )}

            {draft.examiner_traps.length > 0 && (
              <>
                <h3 style={{ fontSize: 13, margin: '10px 0 4px' }}>Examiner traps</h3>
                <ul>
                  {draft.examiner_traps.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </>
            )}

            <div className="row-actions" style={{ marginTop: 12 }}>
              <button
                className="primary"
                type="button"
                onClick={() => save.mutate()}
                disabled={save.isPending}
                data-testid="button-new-question-save"
              >
                {save.isPending ? 'Saving…' : 'Save as DRAFT'}
              </button>
              <button
                type="button"
                onClick={() => generate.mutate()}
                disabled={generate.isPending || save.isPending}
                data-testid="button-new-question-regenerate"
              >
                Regenerate
              </button>
              {save.isError && <span className="err">Save failed</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
